import { px } from '@kerfjs/ui/css-values';
import { EmptyState } from '@kerfjs/ui/empty-state';
import { Grid } from '@kerfjs/ui/grid';
import { List } from '@kerfjs/ui/list';
import { LucideIcon } from '@kerfjs/ui/lucide-icon';
import type { SafeHtml } from 'kerfjs';
import { Paperclip } from 'lucide';

import type { DemandNote } from '../../../../shared/demand-notes.js';
import { DemandNotes } from '../../../components/demand-notes/index.js';
import { editingNoteId, type IntakeDoc, me } from '../../../data/state.js';
import { settledAttachments } from '../../../lib/attachment-processing.js';
import { DocTile } from './doc-tile.js';

/** Documents segment (HS2-YP7T7Q, HS2-WBPZG9): the one inventory for every file
 *  attached to the demand, with user-facing type, selection actions, and notes. */
export function FilesSegment({
  filesByKind,
  notes,
  readOnly,
  notesReadOnly = readOnly,
}: {
  filesByKind: Record<string, IntakeDoc[]>;
  /** The demand's notes (HS2-E33XQD); this segment lists the demand-level ones. */
  notes: readonly DemandNote[];
  readOnly: boolean;
  /** Notes are a demand detail: still editable on a decided demand whose files are locked (HS2-Y50H8E). */
  notesReadOnly?: boolean;
}): SafeHtml {
  const docs = settledAttachments(Object.values(filesByKind).flat());
  return (
    <List className="files-segment" gap="l" controlInsets="tb">
      <input
        class="app-visually-hidden"
        type="file"
        aria-label="Replacement Document"
        data-document-replace-input
        hidden
      />
      {docs.length === 0 ? (
        // One Kerf EmptyState, like Line items and Quotes (HS2-KPNPV5); the box itself
        // is the drop target.
        readOnly ? (
          <EmptyState title="No Documents" />
        ) : (
          <EmptyState
            title="Drop a requisition, quote, drawing, photo, spec, or other file here."
            detail="Each document's type is detected automatically."
            icon={<LucideIcon icon={Paperclip} name="" />}
          />
        )
      ) : (
        // Kerf's labeled multi-select tile grid (HS2-SBCVFD, KF-Z2NT87): each tile is
        // a row whose gridcell holds the nested "Add as quote" button, and arrow
        // keys follow the rendered columns.
        <Grid
          minColumnWidth={px(150)}
          autoFill
          gap={{ column: 'none', row: 's' }}
          flex
          selectionMode="multiple"
          ariaLabel="Demand Documents"
          rootAttributes={{ 'data-docs-grid': '' }}
        >
          {docs.map((document) => (
            <DocTile document={document} readOnly={readOnly} />
          ))}
        </Grid>
      )}

      <DemandNotes
        notes={notes}
        readOnly={notesReadOnly}
        memberId={me.value?.memberId}
        editingNoteId={editingNoteId.value}
        headingId="notes-label"
      />
    </List>
  );
}
